import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { api, errorMessage, unwrap } from "@/api/client";
import { ME_KEY, useMe } from "@/auth";
import Icon from "@/components/icon";
import IconBadge from "@/components/icon-badge";
import Text from "@/components/text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LoginPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/runs";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const login = useMutation({
    mutationFn: () => unwrap(api.POST("/api/auth/login", { body: { username, password } })),
    onSuccess: (data) => {
      queryClient.setQueryData(ME_KEY, data);
      navigate(from, { replace: true });
    },
  });

  if (me.data) return <Navigate to={from} replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <form onSubmit={submit} className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <IconBadge tone="blue" className="h-14 rounded-xl">
            <Icon name="accounts" variant="Bold" size={30} />
          </IconBadge>
          <div className="flex flex-col gap-1">
            <Text.Title>Bulk Register</Text.Title>
            <Text.Hint>Sign in with the account an admin gave you.</Text.Hint>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="username" className="ps-4">
              Username
            </Label>
            <Input
              id="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password" className="ps-4">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {login.isError && <Text.Danger className="ps-4">{errorMessage(login.error)}</Text.Danger>}
        </div>

        <Button type="submit" size="lg" fullWidth loading={login.isPending}>
          Sign in
        </Button>
      </form>
    </div>
  );
}
